import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { authHeaders } from "../components/deck/LoginGate";
import { BASE, compute, withStageCosts } from "./model";
import { MK_BASE } from "../components/sections/market/data";

const DeckCtx = createContext(null);
const SCN = `${process.env.REACT_APP_BACKEND_URL}/api/scenario`;
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const SECTION_LIST = [
  ["overview", "Investment Overview"], ["property", "Property + Site"], ["concept", "Development Concept"],
  ["market", "Market & Leasing"], ["revenue", "Revenue + NOI"], ["budget", "Development Budget"], ["caprate", "Cap Rate Selection"],
  ["valuation", "Valuation"], ["capital", "Capital Stack"], ["exit", "Exit Strategy"], ["jv", "JV Partnership"],
];
export const SECTIONS = SECTION_LIST.map(([id, label], i) => ({ id, label, num: String(i + 1).padStart(2, "0") }));
export const sectionIndex = (id) => SECTIONS.findIndex((s) => s.id === id);

export function DeckProvider({ children }) {
  const [inputs, setInputs] = useState(BASE);
  const [idx, setIdx] = useState(0);
  const [present, setPresent] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [market, setMarketState] = useState(MK_BASE);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${SCN}`, { headers: authHeaders(), withCredentials: true }).then(({ data }) => {
      if (!data.scenario) return;
      setSaved(data.scenario);
      setInputs({ ...BASE, ...data.scenario.inputs });
      setMarketState({ ...MK_BASE, ...data.scenario.market });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(SCN, { inputs, market }, { headers: authHeaders(), withCredentials: true });
      setSaved(data.scenario);
      return true;
    } finally { setSaving(false); }
  };
  const restoreOriginal = async () => {
    await axios.delete(SCN, { headers: authHeaders(), withCredentials: true });
    setSaved(null); setInputs(BASE); setMarketState(MK_BASE);
  };
  const setMarket = (k, v) => setMarketState((s) => ({ ...s, [k]: v }));
  const applyInputs = (patch) => setInputs((s) => ({ ...s, ...patch }));

  const setInput = (k, v) =>
    setInputs((s) => {
      const n = { ...s, [k]: v };
      if (k === "debtPct") n.equityPct = Math.max(0, 100 - v);
      if (k === "equityPct") n.debtPct = Math.max(0, 100 - v);
      return n;
    });

  const setItem = (k, id, patch) => setInputs((s) => ({ ...s, [k]: s[k].map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
  const addItem = (k) => setInputs((s) => ({ ...s, [k]: [...s[k], { id: `u${Date.now()}`, label: "New line item", amount: 0, cat: "ae", custom: true }] }));
  const removeItem = (k, id) => setInputs((s) => ({ ...s, [k]: s[k].filter((x) => x.id !== id) }));

  const value = useMemo(() => {
    const eff = withStageCosts(inputs);
    const m = compute(eff);
    const baseIn = saved ? { ...BASE, ...saved.inputs } : BASE;
    const baseMk = saved ? { ...MK_BASE, ...saved.market } : MK_BASE;
    const modified = !same(baseIn, inputs) || !same(baseMk, market);
    const isOriginal = same(BASE, inputs) && same(MK_BASE, market);
    const go = (i) => setIdx(Math.min(Math.max(i, 0), SECTIONS.length - 1));
    return {
      inputs: eff, raw: inputs, setInput, setItem, addItem, removeItem, reset: () => { setInputs(baseIn); setMarketState(baseMk); }, save, saving, saved, restoreOriginal, isOriginal, market, setMarket, applyInputs, m, modified,
      idx, go, next: () => go(idx + 1), prev: () => go(idx - 1),
      present, setPresent, panelOpen, setPanelOpen,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, idx, present, panelOpen, market, saved, saving]);

  return <DeckCtx.Provider value={value}>{children}</DeckCtx.Provider>;
}

export const useDeck = () => useContext(DeckCtx);
